import {
  afterNextRender,
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  effect,
  inject,
  input,
  numberAttribute,
  untracked,
  viewChild,
} from '@angular/core';
import { MMSCAN_CARD } from '@mmscan/angular-ui/card';
import { MmscanThemeService } from '@mmscan/angular-ui/core';
import type { Chart, ChartConfiguration } from 'chart.js';
import { MMSCAN_CHARTS_I18N } from './charts-i18n';

export type MmscanChartType = 'bar' | 'line' | 'pie' | 'doughnut';

export interface MmscanChartDataset {
  /** Series name (legend, tooltip, data table header). */
  label?: string;
  data: number[];
}

export interface MmscanChartData {
  labels: string[];
  datasets: MmscanChartDataset[];
}

/** Series colours, as design-token palette variables so they follow the theme. */
const SERIES = [
  '--mmscan-palette-brand-500',
  '--mmscan-palette-sky-600',
  '--mmscan-palette-green-600',
  '--mmscan-palette-orange-500',
  '--mmscan-palette-neutral-500',
  '--mmscan-palette-brand-300',
  '--mmscan-palette-sky-400',
  '--mmscan-palette-green-400',
];

const SELECTOR_TYPES: Record<string, MmscanChartType> = {
  'MMSCAN-BAR-CHART': 'bar',
  'MMSCAN-LINE-CHART': 'line',
  'MMSCAN-PIE-CHART': 'pie',
  'MMSCAN-DOUGHNUT-CHART': 'doughnut',
};

let chartModule: Promise<typeof import('chart.js')> | undefined;

/** Loads Chart.js once and registers only what these charts use. */
function loadChartJs(): Promise<typeof import('chart.js')> {
  chartModule ??= import('chart.js').then((module) => {
    module.Chart.register(
      module.BarController,
      module.BarElement,
      module.LineController,
      module.LineElement,
      module.PointElement,
      module.PieController,
      module.DoughnutController,
      module.ArcElement,
      module.CategoryScale,
      module.LinearScale,
      module.Legend,
      module.Tooltip
    );
    return module;
  });
  return chartModule;
}

let nextId = 0;

/**
 * Chart in a card, drawn with Chart.js (loaded on demand) in design-token colours that follow
 * light/dark. The chart is an image labelled by its title, followed by a visually hidden data
 * table so screen-reader users get the numbers.
 *
 * Use the typed selectors or set `type`:
 *
 * ```html
 * <mmscan-bar-chart title="Cases by dealer" [data]="byDealer" orientation="horizontal" />
 * <mmscan-doughnut-chart title="Status" [data]="byStatus" footer="Last 30 days" />
 * ```
 */
@Component({
  selector: 'mmscan-chart, mmscan-bar-chart, mmscan-line-chart, mmscan-pie-chart, mmscan-doughnut-chart',
  imports: [...MMSCAN_CARD],
  template: `
    <mmscan-card [variant]="variant()" class="h-full">
      <div mmscanCardHeader>
        <h3 mmscanCardTitle [id]="titleId">{{ title() }}</h3>
      </div>
      <div mmscanCardContent class="relative" [style.height.rem]="height()">
        <canvas #canvas role="img" [attr.aria-labelledby]="titleId"></canvas>
      </div>
      <!-- sr-only on a wrapper: a table's <caption> is drawn outside the table box, so hiding the table alone leaks it. -->
      <div class="sr-only">
        <table>
          <!-- Kept on one line: a wrapped caption carries the line breaks into its text. -->
          <!-- prettier-ignore -->
          <caption>{{ title() }}</caption>
          <thead>
            <tr>
              <th scope="col">{{ i18n().category }}</th>
              @for (dataset of data().datasets; track $index) {
                <th scope="col">{{ dataset.label ?? title() }}</th>
              }
            </tr>
          </thead>
          <tbody>
            @for (label of data().labels; track $index; let index = $index) {
              <tr>
                <th scope="row">{{ label }}</th>
                @for (dataset of data().datasets; track $index) {
                  <td>{{ format(dataset.data[index]) }}</td>
                }
              </tr>
            }
          </tbody>
        </table>
      </div>
      @if (footer()) {
        <p class="text-xs text-muted-foreground">{{ footer() }}</p>
      }
    </mmscan-card>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  // `title` is an input here; drop the native attribute so the browser doesn't show it as a tooltip.
  host: { class: 'block', '[attr.title]': 'null' },
})
export class MmscanChart {
  protected readonly i18n = inject(MMSCAN_CHARTS_I18N);
  private readonly theme = inject(MmscanThemeService);
  private readonly canvas = viewChild.required<ElementRef<HTMLCanvasElement>>('canvas');
  private readonly hostType = SELECTOR_TYPES[inject<ElementRef<HTMLElement>>(ElementRef).nativeElement.tagName];
  private chart?: Chart;
  protected readonly titleId = `mmscan-chart-title-${nextId++}`;

  readonly data = input.required<MmscanChartData>();
  /** Accessible name and card title. */
  readonly title = input.required<string>();
  readonly footer = input<string>();
  /** Defaults from the selector (`mmscan-bar-chart` → `bar`); required on `mmscan-chart`. */
  readonly type = input<MmscanChartType>();
  /** Bar charts: `horizontal` draws bars left to right. */
  readonly orientation = input<'vertical' | 'horizontal'>('vertical');
  /** Bar charts: stack the datasets. */
  readonly stacked = input(false, { transform: booleanAttribute });
  /** Chart area height, in rem. */
  readonly height = input(18, { transform: numberAttribute });
  /** Formats numbers in tooltips and the data table (e.g. currency). */
  readonly valueFormat = input<(value: number) => string>((value) => new Intl.NumberFormat().format(value));
  readonly variant = input<'surface' | 'outline' | 'elevated'>('surface');

  protected readonly resolvedType = computed<MmscanChartType>(() => this.type() ?? this.hostType ?? 'bar');

  /** Chart.js configuration; recomputed when data, options or the theme change. */
  readonly config = computed<ChartConfiguration>(() => {
    this.theme.theme();
    const css = typeof document === 'undefined' ? null : getComputedStyle(document.documentElement);
    const token = (name: string, fallback: string) => css?.getPropertyValue(name).trim() || fallback;
    const text = token('--mmscan-color-foreground', '#404040');
    const muted = token('--mmscan-color-muted-foreground', '#525252');
    const grid = token('--mmscan-color-border', '#e5e5e5');
    const background = token('--mmscan-color-background', '#ffffff');
    const series = SERIES.map((name, index) =>
      token(name, ['#ed0000', '#0284c7', '#16a34a', '#f97316'][index % 4] ?? '#737373')
    );
    const type = this.resolvedType();
    const circular = type === 'pie' || type === 'doughnut';
    const data = this.data();
    const format = this.valueFormat();
    const reducedMotion = typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;

    return {
      type,
      data: {
        labels: data.labels,
        datasets: data.datasets.map((dataset, index) => {
          const color = series[index % series.length] ?? '#737373';
          return circular
            ? {
                label: dataset.label,
                data: dataset.data,
                backgroundColor: data.labels.map((_, slice) => series[slice % series.length]),
                borderColor: background,
                borderWidth: 2,
              }
            : {
                label: dataset.label,
                data: dataset.data,
                backgroundColor: color,
                borderColor: color,
                borderWidth: type === 'line' ? 2 : 0,
                borderRadius: type === 'bar' ? 4 : 0,
                pointRadius: 3,
                tension: 0.3,
              };
        }),
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: reducedMotion ? false : undefined,
        indexAxis: type === 'bar' && this.orientation() === 'horizontal' ? 'y' : 'x',
        font: { family: token('--mmscan-font-family-sans', 'sans-serif') },
        plugins: {
          legend: {
            display: circular || data.datasets.length > 1,
            position: 'bottom',
            labels: { color: text, usePointStyle: true },
          },
          tooltip: {
            callbacks: {
              label: (item) => `${item.dataset.label ? `${item.dataset.label}: ` : ''}${format(Number(item.raw))}`,
            },
          },
        },
        scales: circular
          ? {}
          : {
              x: { stacked: this.stacked(), ticks: { color: muted }, grid: { color: grid } },
              y: { stacked: this.stacked(), ticks: { color: muted }, grid: { color: grid } },
            },
      },
    } as ChartConfiguration;
  });

  constructor() {
    afterNextRender(() => {
      void loadChartJs().then(({ Chart }) => {
        const context = this.canvas().nativeElement.getContext('2d');
        if (!context) return; // No canvas support (e.g. tests): the data table still renders.
        this.chart = new Chart(
          context,
          untracked(() => this.config())
        );
      });
    });
    effect(() => {
      const config = this.config();
      untracked(() => {
        if (!this.chart) return;
        this.chart.data = config.data;
        this.chart.options = config.options ?? {};
        this.chart.update();
      });
    });
    inject(DestroyRef).onDestroy(() => this.chart?.destroy());
  }

  protected format(value: number | undefined): string {
    return value === undefined ? '' : this.valueFormat()(value);
  }
}
